import { PartnerBusinessHours } from '@provision/entity-types';
import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { QueryVariables, Records } from 'src/graphql/types';
import { DEFAULT_FROM, DEFAULT_TO, TIME_FORMAT } from 'src/pages';

const PARTNER_BUSINESS_HOURS_FIELDS = gql`
  fragment PartnerBusinessHoursFields on PartnerBusinessHours {
    id
    partnerBusinessHoursId
    partnerId
    weekDay
    timeFrom
    timeTo
    createdAt
    updatedAt
  }
`;

const GET_PARTNER_BUSINESS_HOURS_QUERY = gql`
  query GetPartnerBusinessHours($params: QueryParams) @entity(name: "PartnerBusinessHours") {
    getPartnerBusinessHours(queryParams: $params) {
      records {
        ...PartnerBusinessHoursFields
      }
    }
  }
  ${PARTNER_BUSINESS_HOURS_FIELDS}
`;

const DELETE_PARTNER_BUSINESS_HOURS_QUERY = gql`
  mutation DeletePartnerBusinessHours($partnerBusinessHoursIds: String!) {
    deletePartnerBusinessHours(
      conditions: [
        {
          attribute: "partnerBusinessHoursId"
          operator: "in"
          value: $partnerBusinessHoursIds
        }
      ]
    ) {
      ...PartnerBusinessHoursFields
    }
  }
  ${PARTNER_BUSINESS_HOURS_FIELDS}
`;

const CREATE_PARTNER_BUSINESS_HOURS_QUERY = gql`
  mutation CreatePartnerBusinessHours(
    $partnerId: Int!
    $weekDay: Int!
    $timeFrom: String!
    $timeTo: String!
  ) {
    createPartnerBusinessHours(
      data: { partnerId: $partnerId, weekDay: $weekDay, timeFrom: $timeFrom, timeTo: $timeTo }
    ) {
      ...PartnerBusinessHoursFields
    }
  }
  ${PARTNER_BUSINESS_HOURS_FIELDS}
`;

const UPDATE_PARTNER_BUSINESS_HOURS_QUERY = gql`
  mutation UpdatePartnerBusinessHours(
    $partnerBusinessHoursId: String!
    $timeFrom: String
    $timeTo: String
  ) {
    updatePartnerBusinessHours(
      conditions: [
        { attribute: "partnerBusinessHoursId", operator: "=", value: $partnerBusinessHoursId }
      ]
      data: { timeFrom: $timeFrom, timeTo: $timeTo }
    ) {
      ...PartnerBusinessHoursFields
    }
  }
  ${PARTNER_BUSINESS_HOURS_FIELDS}
`;

export interface GetPartnerBusinessHours {
  getPartnerBusinessHours: Records<Partial<PartnerBusinessHours>>;
}

export interface DeletePartnerBusinessHours {
  deletePartnerBusinessHours: Partial<PartnerBusinessHours>[];
}

export interface CreatePartnerBusinessHours {
  createPartnerBusinessHours: Partial<PartnerBusinessHours>[];
}

export interface UpdatePartnerBusinessHours {
  updatePartnerBusinessHours: Partial<PartnerBusinessHours>[];
}

export const usePartnerBusinessHours = (
  params: { partnerId?: number },
  options?: QueryHookOptions<GetPartnerBusinessHours>,
) => {
  const { data, loading, refetch } = useQuery<GetPartnerBusinessHours, QueryVariables>(
    GET_PARTNER_BUSINESS_HOURS_QUERY,
    {
      ...options,
      variables: {
        params: {
          filters: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: params.partnerId?.toString()!,
            },
          ],
        },
      },
      skip: isNullish(params.partnerId),
      context: {
        includePlatformLevelData: true,
      },
    },
  );

  const partnerBusinessHours = useMemo(
    () => data?.getPartnerBusinessHours?.records || [],
    [data?.getPartnerBusinessHours?.records],
  );

  return {
    partnerBusinessHours,
    refetchPartnerBusinessHours: refetch,
    loading,
    isLoaded: !!data?.getPartnerBusinessHours?.records,
  };
};

export const usePartnerBusinessHoursCUDOperations = () => {
  const [deletePartnerBusinessHours, { loading: loadingDeletePartnerBusinessHours }] =
    useMutation<DeletePartnerBusinessHours>(DELETE_PARTNER_BUSINESS_HOURS_QUERY, {
      update(cache, { data }) {
        if (isNullish(data?.deletePartnerBusinessHours?.[0]?.partnerId)) {
          return;
        }

        const id = cache.identify({
          __typename: 'PartnerBusinessHours',
          id: data.deletePartnerBusinessHours[0].partnerBusinessHoursId,
        });

        cache.evict({ id });
        cache.gc();
      },
    });

  const [createPartnerBusinessHours, { loading: loadingCreatePartnerBusinessHours }] =
    useMutation<CreatePartnerBusinessHours>(CREATE_PARTNER_BUSINESS_HOURS_QUERY, {
      update(cache, { data }) {
        if (isNullish(data?.createPartnerBusinessHours?.[0]?.partnerId)) {
          return;
        }

        cache.updateQuery<GetPartnerBusinessHours, QueryVariables>(
          {
            query: GET_PARTNER_BUSINESS_HOURS_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'partnerId',
                    operator: '=',
                    value: data.createPartnerBusinessHours[0].partnerId.toString(),
                  },
                ],
              },
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getPartnerBusinessHours: {
                __typename: 'PartnerBusinessHoursDataResponse',
                records: [
                  ...existingData.getPartnerBusinessHours.records,
                  data.createPartnerBusinessHours[0],
                ],
              },
            };
          },
        );
      },
    });

  const [updatePartnerBusinessHours, { loading: loadingUpdatePartnerBusinessHours }] =
    useMutation<UpdatePartnerBusinessHours>(UPDATE_PARTNER_BUSINESS_HOURS_QUERY, {
      update(cache, { data }) {
        if (isNullish(data?.updatePartnerBusinessHours?.[0]?.partnerId)) {
          return;
        }

        cache.updateQuery<GetPartnerBusinessHours, QueryVariables>(
          {
            query: GET_PARTNER_BUSINESS_HOURS_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'partnerId',
                    operator: '=',
                    value: data.updatePartnerBusinessHours[0].partnerId.toString(),
                  },
                ],
              },
            },
          },
          (existingData) => {
            if (!existingData) return;

            return {
              getPartnerBusinessHours: {
                __typename: 'PartnerBusinessHoursDataResponse',
                records: existingData.getPartnerBusinessHours.records.map((record) =>
                  record.partnerBusinessHoursId ===
                  data.updatePartnerBusinessHours[0].partnerBusinessHoursId
                    ? data.updatePartnerBusinessHours[0]
                    : record,
                ),
              },
            };
          },
        );
      },
    });

  const updateWithOptimisticResponse = useCallback(
    (
      id: string,
      inputType: 'from' | 'to',
      formattedTime: string,
      baseObject: Partial<PartnerBusinessHours> | undefined,
      // eslint-disable-next-line max-params
    ) => {
      void updatePartnerBusinessHours({
        variables: {
          partnerBusinessHoursId: id,
          [inputType === 'from' ? 'timeFrom' : 'timeTo']: formattedTime,
        },
        optimisticResponse: {
          updatePartnerBusinessHours: [
            {
              ...baseObject,
              id,
              ...(inputType === 'from'
                ? { timeFrom: formattedTime }
                : { timeTo: formattedTime }),
            },
          ],
        },
      });
    },
    [updatePartnerBusinessHours],
  );

  const getOptimisticResponseForCreate = useCallback((partnerId: number, weekDay: number) => {
    const tempId = `temp-${crypto.randomUUID()}`;
    return {
      __typename: 'PartnerBusinessHours',
      id: tempId,
      partnerBusinessHoursId: tempId,
      partnerId,
      weekDay,
      timeFrom: dayjs(DEFAULT_FROM).format(TIME_FORMAT),
      timeTo: dayjs(DEFAULT_TO).format(TIME_FORMAT),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, []);

  const createWithOptimisticResponse = useCallback(
    (selectedPartnerId: number | null, weekDay: number) => {
      if (isNullish(selectedPartnerId)) return;

      return createPartnerBusinessHours({
        variables: {
          partnerId: selectedPartnerId,
          weekDay,
          timeFrom: dayjs(DEFAULT_FROM).format(TIME_FORMAT),
          timeTo: dayjs(DEFAULT_TO).format(TIME_FORMAT),
        },
        optimisticResponse: () => {
          return {
            createPartnerBusinessHours: [
              getOptimisticResponseForCreate(selectedPartnerId, weekDay),
            ],
          };
        },
      });
    },
    [createPartnerBusinessHours, getOptimisticResponseForCreate],
  );

  const createWithTimes = useCallback(
    (params: {
      partnerId: number;
      weekDay: number;
      timeFrom: string;
      timeTo: string;
    }) => {
      return createPartnerBusinessHours({
        variables: params,
      });
    },
    [createPartnerBusinessHours],
  );

  const deleteWithOptimisticResponse = useCallback(
    (data: PartnerBusinessHours | PartnerBusinessHours[]) => {
      const entries = Array.isArray(data) ? data : [data];
      return deletePartnerBusinessHours({
        variables: {
          partnerBusinessHoursIds: entries.map((it) => it.id).join(','),
        },
        optimisticResponse: {
          deletePartnerBusinessHours: entries,
        },
      });
    },
    [deletePartnerBusinessHours],
  );

  const isLoading =
    loadingCreatePartnerBusinessHours ||
    loadingUpdatePartnerBusinessHours ||
    loadingDeletePartnerBusinessHours;

  return {
    deletePartnerBusinessHours: deleteWithOptimisticResponse,
    createPartnerBusinessHours: createWithOptimisticResponse,
    createPartnerBusinessHoursWithTimes: createWithTimes,
    updatePartnerBusinessHours: updateWithOptimisticResponse,
    getBusinessHoursCreateOptimisticResponse: getOptimisticResponseForCreate,
    isLoading,
  };
};
